import { isFilterEmpty } from "utils/filters";
import { resetDirty } from "@frui.ts/dirtycheck";
import { bound } from "@frui.ts/helpers";
import type { ScreenBase } from "@frui.ts/screens";
import { Router, watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import type { EntityNames } from "config";
import { Modules } from "config";
import omit from "lodash/omit";
import { action, computed, observable, reaction, runInAction, when } from "mobx";
import Distributor from "entities/distributor";
import DistributorPaymentRequestResponse from "entities/distributorPaymentRequestResponse";
import DistributorRefundCandidateItemStatus from "entities/distributorRefundCandidateItemStatus";
import type DistributorRefundCandidateResponse from "entities/distributorRefundCandidateResponse";
import type DistributorRefundCandidateSummaryResponse from "entities/distributorRefundCandidateSummaryResponse";
import DistributorRefundsRepository from "repositories/distributorRefundsRepository";
import EventBus, { distributorPaymentRequestChecked, distributorRefundGenerated } from "services/eventBus";
import OptionsService from "services/optionsService";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import type { EntityValidationRules } from "services/validationService";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import PriceListService from "services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";
import emptyListResponse from "utils/emptyListResponse";
import ListViewModel from "../../../listViewModel";
import { SelectableListViewModel } from "../../../selectableListViewModel";
import Selectable from "../../../selectableListViewModel";
import type DistributorRefundCandidateApprovalRejectionReason from "entities/distributorRefundCandidateApprovalRejectionReason";

const navigationName = "distributor-refunds";

export class DistributorRefundsFilter {
  distributorId: string;
  paymentRequestId?: string;
}

const DistributorRefundsFilterValidationRules: EntityValidationRules<DistributorRefundsFilter> = {
  distributorId: {
    required: true,
  },
  paymentRequestId: {
    required: true,
  },
};

@Router.registerRoute({ route: navigationName, name: Router.Self })
@Selectable
export default class DistributorRefundsViewModel extends ListViewModel<
  DistributorRefundCandidateResponse,
  ScreenBase,
  ScreenBase,
  DistributorRefundsFilter
> {
  protected readonly modelName: EntityNames = "distributorRefundCandidatesResponse";
  @observable private canGenerateRefund: boolean;
  @observable distributorRefundCandidates: DistributorRefundCandidateSummaryResponse[] = [];

  @computed
  get self(): SelectableListViewModel<DistributorRefundsViewModel, DistributorRefundCandidateResponse> {
    return this as any;
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  @computed
  get paymentRequestOptions() {
    if (this.filter.distributorId) {
      return this.optionsService.entityOptions(DistributorPaymentRequestResponse);
    }
    return [];
  }

  @computed
  get distributorHasNoPaymentRequests() {
    return (
      this.filter.distributorId &&
      !this.paymentRequestOptions.length &&
      this.optionsService.optionsViewModel(DistributorPaymentRequestResponse).isLoaded
    );
  }

  @computed
  get filterIsNotEmpty() {
    return !Object.values(omit(this.appliedFilter, "fullTextSearchEnabled") || {}).every(item => item === undefined);
  }

  @computed
  get isUserAllowedToEdit() {
    return this.securityService.allowTo("finance.factualauditing", "write");
  }

  @computed
  get isApprovalAllowed() {
    return this.self.selectedItems.size > 0;
  }

  @computed
  get isRefundGenerationAllowed() {
    return this.items.some(item => item.status === DistributorRefundCandidateItemStatus.Approved);
  }

  @computed
  get isDeleteAllowed() {
    return this.items.length > 0 && !this.items.some(item => item.status === DistributorRefundCandidateItemStatus.Processed);
  }

  @computed
  get refundAmount() {
    return this.items.reduce((sum, item) => {
      if (item.status === DistributorRefundCandidateItemStatus.Approved) {
        sum += item.amount;
      }

      return sum;
    }, 0);
  }

  @computed
  get totalRefundAmount() {
    return this.items.reduce((sum, item) => {
      sum += item.amount;
      return sum;
    }, 0);
  }

  constructor(
    protected localizationService: LocalizationService,
    protected repository: DistributorRefundsRepository,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected confirmationService: ConfirmationService,
    private optionsService: OptionsService,
    private enumService: EnumService,
    private priceListService: PriceListService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @watchBusy
  async onInitialize() {
    this.loadRefundCandidates();
    await when(() => this.distributorOptions.length !== 0, { timeout: 5000 });

    this.unsubscriptions.push(
      reaction(() => this.filter.distributorId, this.onDistributorChange, { fireImmediately: true }),
      this.eventBus.subscribe(distributorPaymentRequestChecked, this.onPaymentRequestChecked)
    );
    super.onInitialize();
  }

  protected onActivate(): Promise<void> {
    this.loadRefundCandidates();
    return super.onActivate();
  }

  @action.bound
  @watchBusy
  async onApproveClick() {
    const { distributorId, paymentRequestId } = this.filter;
    if (distributorId && paymentRequestId) {
      const distributorRefundCandidateItemIds = Array.from(this.self.selectedItems).map(item => item.id);
      try {
        await this.repository.postApiV1CallCenterDistributorsDistributorIdPaymentRequestsPaymentRequestIdRefundCandidatesApprovals(
          distributorId,
          paymentRequestId,
          { distributorRefundCandidateItemIds }
        );

        this.self.allItems = false;
        this.loadData();
      } catch (e) {
        // TODO
      }
    }
  }

  @action.bound
  @watchBusy
  async onGenerateRefundClick() {
    const { distributorId, paymentRequestId } = this.filter;
    if (distributorId && paymentRequestId) {
      const confirmed = await this.confirmationService.showConfirmation(this.translate("refundConfirmation"), {
        title: this.translate("warning"),
        confirmButton: { text: this.translate("generate") },
      });

      if (confirmed) {
        try {
          await this.repository.postApiV1CallCenterDistributorsDistributorIdPaymentRequestsPaymentRequestIdRefundCandidatesRefunds(
            distributorId,
            paymentRequestId
          );
          this.loadRefundCandidates();
          this.loadData();
          this.eventBus.publish(distributorRefundGenerated());
        } catch (e) {
          // TODO
        }
      }
    }
  }

  @action.bound
  @watchBusy
  async onDeleteRecordsClick() {
    const { distributorId, paymentRequestId } = this.filter;

    if (distributorId && paymentRequestId) {
      const confirmed = await this.confirmationService.showConfirmation(this.translate("deleteConfirmation"), {
        title: this.translate("warning"),
        confirmButton: { text: this.translate("delete"), variant: "danger" },
      });

      if (confirmed) {
        try {
          await this.repository.deleteApiV1CallCenterDistributorsDistributorIdPaymentRequestsPaymentRequestIdRefundCandidates(
            distributorId,
            paymentRequestId
          );
          this.loadRefundCandidates();
          this.loadData();
        } catch (e) {
          // TODO
        }
      }
    }
  }

  @action.bound
  onPaymentRequestClick(paymentRequest: DistributorPaymentRequestResponse) {
    // Changing distributorId filter triggers loading paymentRequestOptions for the distributor
    // Set up self-disposing reaction for updated paymentRequestOptions to set the second filter
    const disposeReaction = reaction(
      () => [
        this.paymentRequestOptions,
        paymentRequest.distributorId,
        paymentRequest.id,
        this.filter.distributorId,
        this.filter.paymentRequestId,
      ],
      () => {
        runInAction(() => {
          this.filter.paymentRequestId = paymentRequest.id;
          this.applyFilterAndLoad();
          disposeReaction();
        });
      }
    );

    // Set distributorId filter and try to set paymentRequestId filter
    // If the distributorId changes it should trigger the reaction above
    runInAction(() => {
      this.filter.distributorId = paymentRequest.distributorId;
      this.applyFilter();
      this.filter.paymentRequestId = paymentRequest.id;
      this.applyFilterAndLoad();
    });
  }

  protected async loadRefundCandidates() {
    this.distributorRefundCandidates = (await this.repository.getApiV1CallCenterDistributorsRefundCandidates()).items;
  }

  protected async fetchData() {
    if (this.filterIsNotEmpty && this.filter.paymentRequestId) {
      const { items, canGenerateRefund } =
        await this.repository.getApiV1CallCenterDistributorsDistributorIdPaymentRequestsPaymentRequestIdRefundCandidates(
          this.filter.distributorId,
          this.filter.paymentRequestId
        );

      this.canGenerateRefund = canGenerateRefund;

      return { items };
    } else {
      return emptyListResponse();
    }
  }

  @bound
  @watchBusy
  private onDistributorChange() {
    this.filter.paymentRequestId = undefined;
    if (this.filter.distributorId) {
      this.optionsService.setParams(DistributorPaymentRequestResponse, { distributorId: this.filter.distributorId });
    }
  }

  @bound
  onPaymentRequestChecked(event: ReturnType<typeof distributorPaymentRequestChecked>) {
    const paymentRequestId = event.payload.paymentRequestId;

    if (this.appliedFilter.paymentRequestId === paymentRequestId) {
      this.loadData();
    }
    this.loadRefundCandidates();
  }

  protected resetFilterValues(filter: DistributorRefundsFilter) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);
  }

  // Method overload to prevent validate filters when are empty
  applyFilter() {
    if (!isFilterEmpty(this.filter) && !validate(this.filter)) {
      return false;
    }

    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  protected createFilter(): DistributorRefundsFilter {
    return attachAutomaticValidator(new DistributorRefundsFilter(), DistributorRefundsFilterValidationRules, false);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `distributorRefunds.${code}`, size);
  }

  translateEnum(
    enumName: "distributorRefundCandidateApprovalRejectionReason",
    value: DistributorRefundCandidateApprovalRejectionReason | undefined
  ) {
    return this.localizationService.translateEnum(enumName as any, value as any);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.code;
  }

  translateDistributor(distributorId: string) {
    return this.optionsService.entityOptions(Distributor).find(item => item.id === distributorId)?.label ?? "";
  }

  translateChargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }
}
